/**
 * Badges, small rows and helpers shared by the Sites pages (JSS#1157, split
 * out of the old pages/Deployments.tsx).
 */
import { useAuth } from '@/contexts/AuthContext';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { JbsStatus, Site, SiteStatus } from '@/types';

// ---- Status Badges ----

// A site's status follows its readiness checks (#796): `degraded` means an
// active site's checks keep failing, and `reason` names them.
export function SiteStatusBadge({ status, reason }: { status: SiteStatus; reason?: string | null }) {
  const colors: Record<SiteStatus, string> = {
    provisioning: 'bg-status-warning/20 text-status-warning',
    active: 'bg-status-healthy/20 text-status-healthy',
    degraded: 'bg-status-error/20 text-status-error',
    offline: 'bg-silver-muted/20 text-silver-muted',
    decommissioning: 'bg-silver-muted/20 text-silver-muted',
  };

  return (
    <span
      className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[status])}
      title={reason ?? undefined}
      data-testid="site-status-badge"
    >
      {status}
    </span>
  );
}

/** Statuses whose cluster is installed and reachable: a degraded site still runs its cluster. */
export function siteClusterInstalled(status: SiteStatus): boolean {
  return status === 'active' || status === 'degraded';
}

/**
 * On a degraded site (#796): why, from its failing readiness checks. The
 * Overview's "Ready for JBS" panel details them with their remediation.
 */
export function SiteDegradedNotice({ site }: { site: Pick<Site, 'status' | 'statusReason'> }) {
  if (site.status !== 'degraded') return null;
  return (
    <div
      className="flex items-start gap-2 rounded-lg border border-status-error/30 bg-status-error/10 px-4 py-2.5 text-xs text-status-error"
      data-testid="site-status-reason"
    >
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <span>
        This site is degraded: its readiness checks have kept failing.{' '}
        {site.statusReason ?? 'See Ready for JBS on the Overview tab.'}
      </span>
    </div>
  );
}

/** JBS install status badge — separate from site status, which only
 * reflects platform prereq health. See #164. */
export function JbsStatusBadge({
  status,
  message,
}: {
  status: JbsStatus;
  message: string | null;
}) {
  const config: Record<JbsStatus, { label: string; classes: string }> = {
    installed: {
      label: 'installed',
      classes: 'bg-status-healthy/20 text-status-healthy',
    },
    installing: {
      label: 'installing',
      classes: 'bg-status-warning/20 text-status-warning',
    },
    not_installed: {
      label: 'not installed',
      classes: 'bg-silver-muted/20 text-silver-muted',
    },
    failed: {
      label: 'failed',
      classes: 'bg-status-error/20 text-status-error',
    },
    unknown: {
      label: 'unknown',
      classes: 'bg-silver-muted/20 text-silver-muted',
    },
  };
  const { label, classes } = config[status];
  return (
    <span
      className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', classes)}
      title={message ?? undefined}
    >
      {label}
    </span>
  );
}

// ---- Helpers ----

export function timeAgo(timestamp: string | null): string {
  if (!timestamp) return '';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function formatAge(isoDate: string | null): string {
  if (!isoDate) return '--';
  const ms = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// ---- Health Indicator ----

export function SiteHealthIndicator({ status }: { status: SiteStatus }) {
  const dotColor = (threshold: SiteStatus[]): string => {
    if (threshold.includes(status)) return 'bg-status-healthy';
    if (status === 'degraded') return 'bg-status-error';
    if (status === 'provisioning') return 'bg-status-warning';
    return 'bg-silver-muted/30';
  };

  return (
    <div className="flex items-center gap-1">
      <span className={cn('w-2 h-2 rounded-full transition-colors', dotColor(['active', 'provisioning', 'degraded']))} />
      <span className={cn('w-2 h-2 rounded-full transition-colors', dotColor(['active', 'provisioning']))} />
      <span className={cn('w-2 h-2 rounded-full transition-colors', dotColor(['active']))} />
    </div>
  );
}

// ---- Info Row ----

export function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs text-silver-muted shrink-0">{label}</span>
      <span
        className={cn(
          'text-sm text-silver-bright text-right break-all',
          mono && 'font-mono text-xs'
        )}
      >
        {value || '--'}
      </span>
    </div>
  );
}

// ---- Role checks ----

export function useSuperUser(): boolean {
  const { isSuperUser } = useAuth();
  return isSuperUser();
}
